// features/workstations/dashboard-sections/macro-conditions.js
// ═══════════════════════════════════════════════════════════
// MACRO CONDITIONS — the backdrop your strategy lives inside
// ═══════════════════════════════════════════════════════════
// Tier C / context. Pairs with Sentiment — together they cover
// the \"where is the domain right now\" scanning layer.
//
// Sentiment = retail mood + positioning + flow
// Macro     = rates + dollar + commodities + econ scoreboard
//
// Reads the macro slice of the trading bundle (same source as
// DI Dashboard). Composed from shared primitives.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
} from '../../../components/widgets/primitives';

// Bundle uses 'mid' / 'hot' / 'cool' / 'neutral' tones across
// different sections. Map everything into the standard 7-color
// token set so PillRow gets consistent tones.
function normalizeTone(tone) {
  const map = {
    mid:     'steel',
    dim:     'steel',
    neutral: 'steel',
    hot:     'terra',
    cool:    'sage',
    gold:    'gold',
    sage:    'sage',
    terra:   'terra',
    amber:   'amber',
    teal:    'teal',
    purple:  'purple',
  };
  return map[tone] || 'steel';
}

// Direction arrows for yield-curve rows
function directionGlyph(dir) {
  if (dir === 'up') return '↑';
  if (dir === 'dn' || dir === 'down') return '↓';
  return '•';
}

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function MacroConditionsSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/macro-conditions?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="ws-macro">
        <div className="ws-macro-loading">Loading macro conditions…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="ws-macro">
        <Eyebrow>macro — read failed</Eyebrow>
        <SerifSummary>{error || 'No data returned.'}</SerifSummary>
      </section>
    );
  }

  return <MacroBody data={data} />;
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function MacroBody({ data }) {
  const curve = Array.isArray(data.yield_curve) ? data.yield_curve : [];
  const rates = Array.isArray(data.rates) ? data.rates : [];
  const currency = Array.isArray(data.currency) ? data.currency : [];
  const commodities = Array.isArray(data.commodities) ? data.commodities : [];
  const econ = Array.isArray(data.scoreboard_picks) ? data.scoreboard_picks : [];

  // Headline: 2/10 spread + inversion tag if present
  const spreadRow = rates.find(r => /2\/10 Spread|2–2 Spread/i.test(r.label || ''));
  const fedFunds = rates.find(r => /Fed Funds/i.test(r.label || ''));
  const dxy = currency.find(r => /DXY/i.test(r.label || ''));
  const cpi = econ.find(r => /^CPI/i.test(r.label || ''));

  const headlineParts = [];
  if (spreadRow) headlineParts.push(spreadRow.value);
  if (fedFunds) headlineParts.push(`Fed ${fedFunds.value}`);
  if (dxy) headlineParts.push(`DXY ${dxy.value}`);
  const headline = headlineParts.length
    ? headlineParts.join(' · ')
    : 'Macro snapshot.';

  const summaryParts = [];
  if (cpi) summaryParts.push(`CPI ${cpi.value}`);
  const gold = commodities.find(r => /Gold/i.test(r.label || ''));
  const oil  = commodities.find(r => /WTI|Crude Oil/i.test(r.label || ''));
  if (gold) summaryParts.push(`Gold ${gold.value}`);
  if (oil) summaryParts.push(`Oil ${oil.value}`);
  const summary = summaryParts.length
    ? summaryParts.join(' · ')
    : 'Rates, dollar, commodities, and economic scoreboard in one read.';

  return (
    <section className="ws-macro">
      <Eyebrow parts={[
        'macro conditions',
        data.freshness?.rates?.source === 'live' ? 'live rates' : 'rates sample',
      ]} />

      <SerifHeadline>{headline}</SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {/* Yield curve */}
      {curve.length > 0 && (
        <SectionStrip label="yield curve" count={curve.length}>
          <div className="ws-macro-curve">
            {curve.map((row, i) => (
              <div key={i} className="ws-macro-curve-cell">
                <div className="ws-macro-curve-term">{row.term}</div>
                <div className="ws-macro-curve-rate">{row.rate || ''}</div>
                {row.change && (
                  <div className={`ws-macro-curve-change ws-macro-curve-change-${row.dir || 'up'}`}>
                    {directionGlyph(row.dir)} {row.change}
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionStrip>
      )}

      {/* Rates rows (spread + Fed Funds + mortgage + credit spreads) */}
      {rates.length > 0 && (
        <SectionStrip label="rates & credit" count={rates.length}>
          <PillTable>
            {rates.map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.value || ''}
                tone={normalizeTone(r.tone)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Currency (limit to top 3 to keep the widget tight) */}
      {currency.length > 0 && (
        <SectionStrip label="currency" count={Math.min(3, currency.length)}>
          <PillTable>
            {currency.slice(0, 3).map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.change ? `${r.value || ''} · ${r.change}` : (r.value || '')}
                tone={normalizeTone(r.tone)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Commodities (top 3) */}
      {commodities.length > 0 && (
        <SectionStrip label="commodities" count={Math.min(3, commodities.length)}>
          <PillTable>
            {commodities.slice(0, 3).map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.change ? `${r.value || ''} · ${r.change}` : (r.value || '')}
                tone={normalizeTone(r.tone)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Economic scoreboard — the 3 most-cited cells */}
      {econ.length > 0 && (
        <SectionStrip label="economic scoreboard" count={econ.length}>
          <div className="ws-macro-econ-grid">
            {econ.slice(0, 6).map((cell, i) => (
              <div key={i} className={`ws-macro-econ-cell ws-macro-econ-${normalizeTone(cell.tone)}`}>
                <div className="ws-macro-econ-label">{cell.label}</div>
                <div className="ws-macro-econ-value">{cell.value}</div>
                {cell.trend?.label && (
                  <div className={`ws-macro-econ-trend ws-macro-econ-trend-${normalizeTone(cell.trend.tone)}`}>
                    {cell.trend.label}
                  </div>
                )}
                {cell.ctx && <div className="ws-macro-econ-ctx">{cell.ctx}</div>}
              </div>
            ))}
          </div>
        </SectionStrip>
      )}

      {/* Practitioner lens from the bundle (rates.lens has the
          reading of the curve + credit backdrop) */}
      {data.lens?.body && (
        <PractitionerLens label={data.lens.eyebrow || 'reading the curve'}>
          <span dangerouslySetInnerHTML={{ __html: sanitizeLensHtml(data.lens.body) }} />
        </PractitionerLens>
      )}
    </section>
  );
}

// Strip all HTML except <em> and <strong> — bundle copy only uses
// those. Defensive against seed-content drift.
function sanitizeLensHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<(?!\/?(em|strong)\b)[^>]+>/gi, '');
}
